import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { AthletePreview, athleteDisplayName } from '@strength/domain';
import { MdAdd, MdCancel, MdPlaylistAdd } from '@teamworksdev/icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../../components/drawer';
import { AthleteAvatar } from '../../../../components/icons/athlete-avatar';
import { SearchInput } from '../../../../components/input/search-input';
import { Tabs, TabTrigger } from '../../../../components/tab/tab';
import { cn } from '../../../../components/utils';

export type ApplySwapData = {
  workoutId: string;
  exerciseId: string;
  oldExerciseName: string;
  newExerciseName: string;
  newLibraryExerciseId: string;
  oldLibraryExerciseId: string;
  isBlockLayer: boolean;
  hasFutureInstances: boolean;
  hasMatchingAthletes: boolean;
};

type MatchingAthlete = {
  athlete: AthletePreview;
  hasMatch: boolean;
};

type FilterTab = 'all' | 'selected' | 'unselected';

type ApplySwapDrawerProps = {
  data: ApplySwapData;
  matchingAthletes: MatchingAthlete[];
  onClose: () => void;
  onSave: (options: {
    applyToFutureInstances: boolean;
    applyToOtherAthletes: boolean;
    selectedAthleteIds: string[];
  }) => void;
};

export const ApplySwapDrawer = ({
  data,
  matchingAthletes,
  onClose,
  onSave,
}: ApplySwapDrawerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [applyToFutureInstances, setApplyToFutureInstances] = useState(false);
  const [applyToOtherAthletes, setApplyToOtherAthletes] = useState(false);
  const [selectedAthleteIds, setSelectedAthleteIds] = useState<Set<string>>(
    new Set(),
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState<FilterTab>('all');

  const showFutureInstances = data.hasFutureInstances;
  const showOtherAthletes = !data.isBlockLayer && data.hasMatchingAthletes;

  const hasValidSelection =
    (showFutureInstances && applyToFutureInstances) ||
    (showOtherAthletes && applyToOtherAthletes);

  const filteredAthletes = useMemo(() => {
    let athletes = matchingAthletes;

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      athletes = athletes.filter((entry) =>
        athleteDisplayName(entry.athlete).toLowerCase().includes(term),
      );
    }

    if (filterTab === 'selected') {
      athletes = athletes.filter((entry) =>
        selectedAthleteIds.has(entry.athlete.id),
      );
    } else if (filterTab === 'unselected') {
      athletes = athletes.filter(
        (entry) => !selectedAthleteIds.has(entry.athlete.id),
      );
    }

    return athletes;
  }, [matchingAthletes, searchTerm, filterTab, selectedAthleteIds]);

  const handleSelectAll = () => {
    const newSelected = new Set(selectedAthleteIds);
    matchingAthletes.forEach((entry) => {
      if (entry.hasMatch) {
        newSelected.add(entry.athlete.id);
      }
    });
    setSelectedAthleteIds(newSelected);
  };

  const handleToggleAthlete = (athleteId: string) => {
    const newSelected = new Set(selectedAthleteIds);
    if (newSelected.has(athleteId)) {
      newSelected.delete(athleteId);
    } else {
      newSelected.add(athleteId);
    }
    setSelectedAthleteIds(newSelected);
  };

  const handleSave = () => {
    onSave({
      applyToFutureInstances,
      applyToOtherAthletes,
      selectedAthleteIds: Array.from(selectedAthleteIds),
    });
  };

  const handleApplyToOtherAthletesChange = (checked: boolean) => {
    setApplyToOtherAthletes(checked);
    if (!checked) {
      setSelectedAthleteIds(new Set());
      setSearchTerm('');
      setFilterTab('all');
    }
  };

  return (
    <Drawer open={true} onClose={onClose} anchor="right">
      <DrawerHeader title={t('applyTo')} onClose={onClose} />

      <div className="flex flex-col px-6 py-4 gap-4">
        {/* Subcopy */}
        <Typography variant="body2" color="text.secondary">
          {t('exerciseChangeCannotBeUndone')}
        </Typography>

        {/* Exercise change banner */}
        <div className="rounded-lg bg-gray-100 p-4">
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {t('exerciseChange')}
          </Typography>
          <Typography variant="body1">
            {data.oldExerciseName} → {data.newExerciseName}
          </Typography>
        </div>

        {/* Apply to future instances */}
        {showFutureInstances && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyToFutureInstances}
                  onChange={(e) => setApplyToFutureInstances(e.target.checked)}
                />
              }
              label={t('applyToFutureInstances')}
            />
            {showOtherAthletes && <Divider />}
          </>
        )}

        {/* Apply to other athletes */}
        {showOtherAthletes && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyToOtherAthletes}
                  onChange={(e) =>
                    handleApplyToOtherAthletesChange(e.target.checked)
                  }
                />
              }
              label={t('applyToOtherAthletes')}
            />

            {applyToOtherAthletes && (
              <div className="flex flex-col gap-2">
                <SearchInput
                  label={t('search')}
                  placeholder={t('searchAthlete')}
                  value={searchTerm}
                  onChange={(val) => setSearchTerm(val)}
                />

                <Tabs
                  value={filterTab}
                  onChange={(_, newValue) =>
                    setFilterTab(newValue as FilterTab)
                  }
                  size="small"
                  type="buttonGroup"
                >
                  <TabTrigger label={t('allAthletes')} value="all" />
                  <TabTrigger label={t('selected')} value="selected" />
                  <TabTrigger label={t('unselected')} value="unselected" />
                </Tabs>

                <span className="flex flex-col flex-1 min-h-0">
                  <div
                    className={cn(
                      'overflow-y-auto border rounded flex-1 min-h-[200px] max-h-[400px]',
                    )}
                  >
                    {/* Select all — only shown on "all" tab */}
                    {filterTab === 'all' && (
                      <>
                        <div className="flex justify-end p-1">
                          <Button
                            size="small"
                            variant="text"
                            onClick={handleSelectAll}
                            className="gap-2"
                          >
                            <MdPlaylistAdd size={16} />
                            {t('selectAll')}
                          </Button>
                        </div>
                        <Divider className="mb-2" />
                      </>
                    )}

                    <List
                      dense
                      sx={{
                        padding: 0,
                        '& .MuiListItem-root': {
                          mb: 1,
                          '&:last-child': { mb: 0 },
                        },
                      }}
                    >
                      {filteredAthletes.map(({ athlete, hasMatch }) => {
                        const isSelected = selectedAthleteIds.has(athlete.id);
                        const backgroundColor = isSelected
                          ? grey[200]
                          : 'white';

                        return (
                          <ListItem
                            key={athlete.id}
                            onClick={() => {
                              if (hasMatch) {
                                handleToggleAthlete(athlete.id);
                              }
                            }}
                            secondaryAction={
                              hasMatch ? (
                                isSelected ? (
                                  <MdCancel
                                    size={20}
                                    color={theme.palette.mediumEmphasisText}
                                  />
                                ) : (
                                  <MdAdd
                                    size={20}
                                    color={theme.palette.mediumEmphasisText}
                                  />
                                )
                              ) : undefined
                            }
                            sx={{
                              pointerEvents: hasMatch ? 'auto' : 'none',
                              backgroundColor,
                              opacity: hasMatch ? 1 : 0.7,
                              '&:hover': {
                                backgroundColor:
                                  hasMatch && backgroundColor === 'white'
                                    ? grey[50]
                                    : undefined,
                                cursor: hasMatch ? 'pointer' : 'default',
                              },
                            }}
                          >
                            <ListItemAvatar
                              sx={{ minWidth: 0, paddingRight: '8px' }}
                            >
                              <AthleteAvatar
                                athlete={athlete}
                                sx={{ width: 24, height: 24, fontSize: 12 }}
                                imgSrc={athlete.imageUrl ?? ''}
                              />
                            </ListItemAvatar>
                            <ListItemText
                              primary={athleteDisplayName(athlete)}
                              secondary={
                                !hasMatch
                                  ? t('noExerciseMatchesFound')
                                  : undefined
                              }
                              secondaryTypographyProps={
                                !hasMatch
                                  ? {
                                      sx: {
                                        color: 'error.main',
                                        fontSize: '0.75rem',
                                      },
                                    }
                                  : undefined
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {t('usersSelectedCount', {
                      count: selectedAthleteIds.size,
                    })}
                  </p>
                </span>
              </div>
            )}
          </>
        )}
      </div>

      <DrawerFooter className="flex flex-row justify-end gap-2 p-6">
        <Button onClick={onClose} variant="outlined">
          {t('cancel')}
        </Button>
        <Button disabled={!hasValidSelection} onClick={handleSave}>
          {t('save')}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};
